import { useState, forwardRef, InputHTMLAttributes, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import InputError from './InputError';

interface LabeledCreatableSelectProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value'
> {
  label?: string;
  placeholder?: string;
  options: string[];
  value?: string;
  onChangeValue?: (value: string) => void;
  error?: string;
}

const LabeledCreatableSelect = forwardRef<
  HTMLInputElement,
  LabeledCreatableSelectProps
>(
  (
    {
      label,
      placeholder = 'Type to add a new category',
      options,
      value,
      onChangeValue,
      onChange,
      onFocus,
      onBlur,
      onClick,
      error,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(value ?? '');
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      if (value !== undefined) setInputValue(value);
    }, [value]);

    const query = inputValue.trim().toLowerCase();
    // Show every option when the input already holds an existing option,
    // so a preselected value doesn't hide the other choices.
    const filteredOptions = options.some(
      (opt) => opt.trim().toLowerCase() === query
    )
      ? options
      : options.filter((opt) => opt.toLowerCase().includes(query));

    const handleSelect = (option: string) => {
      setInputValue(option);
      setIsOpen(false);
      onChangeValue?.(option);
    };

    return (
      <fieldset className="form-control w-full relative">
        {label && (
          <legend className="label">
            <span className="fieldset-legend text-primary font-normal text-sm">
              {label}
            </span>
          </legend>
        )}
        <div className="relative">
          <input
            ref={ref}
            type="text"
            autoComplete="off"
            className="input input-lg bg-white placeholder:text-[var(--light-gray)] w-full pr-10"
            placeholder={placeholder}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setIsOpen(true);
              onChangeValue?.(e.target.value);
              onChange?.(e);
            }}
            onFocus={(e) => {
              setIsOpen(true);
              onFocus?.(e);
            }}
            onClick={(e) => {
              setIsOpen(true);
              onClick?.(e);
            }}
            onBlur={(e) => {
              setTimeout(() => setIsOpen(false), 100);
              onBlur?.(e);
            }}
          />
          <ChevronDown
            aria-hidden="true"
            className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
          {isOpen && filteredOptions.length > 0 && (
            <ul className="absolute top-full mt-1 z-10 bg-white border border-gray-300 w-full rounded shadow max-h-40 overflow-auto">
              {filteredOptions.map((option) => (
                <li key={option}>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <InputError error={error} />
      </fieldset>
    );
  }
);

LabeledCreatableSelect.displayName = 'LabeledCreatableSelect';

export default LabeledCreatableSelect;
